// /* eslint-disable react/require-default-props */
/* eslint-disable react/no-unused-prop-types */
import React, { ReactElement, useEffect, useMemo } from 'react';
import { ElearningDataType } from '@/modules/types/elearning';
import {
  QuizAttemptResponseType,
  QuizAttemptStartResponseType,
  QuizDataType,
} from '@/modules/types/quiz';
import { ResourceType } from '@/modules/types/resource';
import { FaqType, HelperType } from '@/modules/types/helper';
import store from './store';
import { Provider, useDispatch, useSelector } from 'react-redux';
import {
  actions as ElearningPlayerActions,
  ElearningPlayerStateType,
} from '@/components/Player/modules/reducers/elearningPlayer';
import { actions as VideoPlayerActions } from '@/components/Player/modules/reducers/videoPlayer';
import cn from 'classnames';
import PlayerLayout from './Layout';
import FrameTopBanner from './FrameTopBanner';
import FrameBottom from './FrameBottom';
import Quiz from './Quiz';
import FrameContent from './FrameContent';
import { hasTabletArmedContent } from './FrameContent/util';
import { FrameDataType } from '@/modules/types/frame';
import { Alert } from 'elmo-elements';
import { ElearningBehaviourSettingsDataInterface } from '@/modules/types/tenant';
import { ErrorBoundary } from 'react-error-boundary';
import { SlideErrorFallback } from '@/lib/Error/slideErrorFallBack';

export interface PlayerUiHandlersPropsType {
  submitResult: (result: any, callback: () => void) => void;
  submitQuestion: (answer: any, callback: (payload: any) => void) => void;
  getQuestions: () => void;
  submitAssessment: (quizData: any, callback: (payload: any) => void) => void;
  startAssessment: (
    getQuizAttemptData: QuizAttemptResponseType | null | undefined,
  ) => void;
  moveToFrame: (nextFrameId: string) => void;
}

interface PlayerUiPropsType {
  isPreview?: boolean;
  isLive?: boolean;
  isPlayable: boolean;
  currentFrameId: string;
  isQuizEnabled: boolean;
  elearningBehaviourSettings: ElearningBehaviourSettingsDataInterface;
  // eslint-disable-next-line react/require-default-props
  elearning?: ElearningDataType;
  // eslint-disable-next-line react/require-default-props
  frames?: FrameDataType[];
  quiz?: QuizDataType;
  resources: ResourceType[];
  helperContent: HelperType[];
  helperFaq: FaqType[];
  handlers?: PlayerUiHandlersPropsType;
  canvas?: ReactElement;
  isMobileView?: boolean;
  vocamVideoLink?: string;
  quizGetAttemptData?: QuizAttemptResponseType | null;
  quizStartAttemptData?: QuizAttemptStartResponseType | null;
}

interface PlayerUiViewPropsType {
  // eslint-disable-next-line react/require-default-props
  canvas?: ReactElement;
  isMobileView?: boolean;
}

const PlayerUiPropsDefault = {
  isPreview: false,
  quiz: undefined,
  handlers: {
    submitResult: (result: any, callback: () => void) => {},
    submitQuestion: (answer: any, callback: (payload: any) => void) => {},
    getQuestions: () => {},
    submitAssessment: (quizData: any, callback: (payload: any) => void) => {},
    startAssessment: () => {},
    moveToFrame: (nextFrameId: string) => {},
  },
  canvas: undefined,
};

const PlayerUiView = ({
  canvas,
  isMobileView,
}: PlayerUiViewPropsType): ReactElement => {
  const { isQuizReady, content } = useSelector(
    (state: { elearningPlayer: ElearningPlayerStateType }) =>
      state.elearningPlayer,
  );

  // course-level: any frame carrying a tablet-armed mobile-fix block puts the
  // whole course in tablet mode, so the chrome doesn't flip on fix-less frames
  const isTabletArmed = useMemo(
    () => hasTabletArmedContent(content.frames),
    [content.frames],
  );

  return (
    <PlayerLayout
      top={<FrameTopBanner isMobileView={isMobileView} />}
      bottom={<FrameBottom isMobileView={isMobileView} />}
      canvas={
        canvas ||
        (isQuizReady ? <Quiz /> : <FrameContent isMobileView={isMobileView} />)
      }
      className={cn('module-viewer__draft', {
        'module-viewer__is-mobile': isMobileView,
        // learner playback only — with a canvas prop this layout wraps the
        // slide editor, where the playback chrome must not pin to the window
        'module-viewer__player': !canvas,
        'module-viewer--tabletfix': !canvas && isTabletArmed,
      })}
      resources={content.resources}
      playerHelper={{
        content: content.helperContent,
        faq: content.helperFaq,
      }}
      currentFrameId={content.currentFrameId}
      elearning={content.elearning}
      frames={content.frames}
    />
  );
};

const PlayerUiInit = ({
  isPreview,
  isLive,
  isPlayable,
  currentFrameId,
  isQuizEnabled,
  elearningBehaviourSettings,
  elearning,
  frames,
  quiz,
  resources,
  helperContent,
  helperFaq,
  handlers,
  canvas,
  isMobileView,
  vocamVideoLink,
  quizGetAttemptData,
  quizStartAttemptData,
}: PlayerUiPropsType): ReactElement => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({
      type: ElearningPlayerActions.setQuizStartAttempt.type,
      payload: quizStartAttemptData,
    });
  }, [quizStartAttemptData]);
  useEffect(() => {
    dispatch({
      type: ElearningPlayerActions.setQuizGetAttempt.type,
      payload: quizGetAttemptData,
    });
  }, [quizGetAttemptData]);

  useEffect(() => {
    dispatch({
      type: ElearningPlayerActions.setContent.type,
      payload: {
        elearning,
        frames,
        currentFrameId,
        quiz,
        resources,
        helperContent,
        helperFaq,
        quizGetAttemptData,
        quizStartAttemptData,
      },
    });
    dispatch({
      type: ElearningPlayerActions.setHandlers.type,
      payload: handlers,
    });

    dispatch({
      type: ElearningPlayerActions.setElearningBehaviourSettings.type,
      payload: elearningBehaviourSettings,
    });

    if (isLive) {
      dispatch({
        type: ElearningPlayerActions.setLive.type,
      });
    }

    if (isPreview)
      dispatch({
        type: ElearningPlayerActions.setPreview.type,
      });

    if (isPlayable)
      dispatch({
        type: ElearningPlayerActions.setPlayable.type,
      });
  }, []);

  useEffect(() => {
    dispatch({
      type: ElearningPlayerActions.setCurrentFrameId.type,
      payload: {
        currentFrameId,
      },
    });
  }, [currentFrameId]);

  useEffect(() => {
    if (isQuizEnabled)
      dispatch({
        type: ElearningPlayerActions.enableQuiz.type,
        payload: true,
      });
  }, [isQuizEnabled]);

  useEffect(() => {
    if (vocamVideoLink) {
      dispatch({
        type: VideoPlayerActions.getVocamVideoLink.type,
        payload: {
          vocamVideoLink,
        },
      });
    }
  }, [dispatch, vocamVideoLink]);

  return <PlayerUiView canvas={canvas} isMobileView={isMobileView} />;
};

const PlayerUi = (props: PlayerUiPropsType) => {
  if (!props.elearning) {
    return (
      <Alert
        message="No Elearning module data"
        type="warning"
        isCloseable={false}
      />
    );
  }
  if (!props.frames && !props.isQuizEnabled) {
    return <Alert message="No Frame data" type="warning" isCloseable={false} />;
  }
  return (
    <ErrorBoundary FallbackComponent={SlideErrorFallback}>
      <Provider store={store}>
        <PlayerUiInit {...props} />
      </Provider>
    </ErrorBoundary>
  );
};
PlayerUi.defaultProps = PlayerUiPropsDefault;

PlayerUiInit.defaultProps = PlayerUiPropsDefault;

export default PlayerUi;
